<template>
  <Fragment>
    <b-card body-class="p-3" class="mb-0">
      <div class="my-3 d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <h5 class="mb-0 font-weight-bold">Bordereaux Match Pending</h5>

          <div class="ml-3 d-flex align-items-center" style="gap: 8px;">
            <BaseButton
              v-if="!!selectedItems.length"
              size="sm"
              class="disabled"
              disabled
              @click="() => (showEditModal = !showEditModal)"
              >Edit</BaseButton
            >
          </div>
        </div>

        <div class="d-flex align-items-center">
          <BaseSearchInput v-model="search" />

          <BDXMatchPendingManagerExportButton />

          <HelperIcon>
            <div class="m-0 font-italic">
              A status of Pending in BDX Match reflects any new BDX submitted
              into LimeIQ for the first time using the LimeIQ Key (UMR + Risk
              Code + Cat Code + Section Number). These items will remain pending
              and will not be able to be viewed or exported until the minimum
              information is completed. Please fill out as many of the fields as
              possible, which can be edited at a later time.
            </div>
          </HelperIcon>
        </div>
      </div>

      <div v-if="activeColumnFilters.length" class="mb-2 d-flex align-items-center flex-wrap active-filters">
        <b-badge
          v-for="filter in activeColumnFilters"
          :key="filter.key"
          class="text-primary border border-primary filter-badge"
          pill
        >
          {{ filter.label }}: {{ filter.value }}
          <a
            tabindex="0"
            role="button"
            class="text-danger ml-1"
            @click.prevent="setColumnFilter(filter.key, '')"
          >
            <b-icon icon="x" :scale="1.5"></b-icon>
          </a>
        </b-badge>
      </div>

      <BDXMatchPendingManagerList
        ref="listRef"
        v-model="selectedItems"
        :active-items="activeItems"
        @edit:item="(item) => setActiveBDX(item)"
      />
    </b-card>

    <BDXEditForm
      v-model="showEditModal"
      :ids="activeItems"
      :preview-item="activeBDX"
      :lead-underwriters="activeBDX.lead_underwriter"
      :follower-underwriters="activeBDX.follower_underwriter"
      :tpa="activeBDX.tpa"
      :broker="activeBDX.broker"
      :coverholder="activeBDX.coverholder"
      :ucr="activeBDX.ucr"
      :fcp-status="activeBDX.fcp_status"
      :fcp-start-date="activeBDX.fcp_start_date"
      :currency-type="activeBDX.currency_type"
      :year-of-account="activeBDX.year_of_account"
      :continuous-contract="activeBDX.continuous_contract"
      :account-name="activeBDX.account_name"
      :umr="activeBDX.umr"
      :account-code="activeBDX.account_code"
      :account-type="activeBDX.account_type"
      :jurisdiction="activeBDX.jurisdiction"
      is-modal
      title="BDX"
      @success="onEditModalSuccess"
    />
  </Fragment>
</template>

<script>
import {
  defineComponent,
  nextTick,
  computed,
  ref,
  provide,
  watch
} from '@vue/composition-api'

const MATCH_COLUMN_LABELS = {
  umr: 'UMR',
  risk_code: 'Risk Code',
  cat_code: 'Cat Code',
  section_no: 'Section #'
}
import { Fragment } from 'vue-fragment'

import { useActiveBDX } from '@composables'

import { BaseButton, BaseSearchInput } from '@components/base'
import { HelperIcon } from '@components/misc'
import { BDXEditForm } from '@components/bdx'

import { BDXMatchPendingManagerInjectionKey } from './BDXMatchPendingManager.constants'
import BDXMatchPendingManagerExportButton from './BDXMatchPendingManagerExportButton.vue'
import BDXMatchPendingManagerList from './BDXMatchPendingManagerList.vue'

export default defineComponent({
  name: 'BDXMatchPendingManager',
  components: {
    Fragment,

    BaseButton,
    BaseSearchInput,

    HelperIcon,

    BDXEditForm,

    BDXMatchPendingManagerExportButton,
    BDXMatchPendingManagerList
  },
  setup() {
    const { activeBDX, setActiveBDX, resetActiveBDX } = useActiveBDX()

    const listRef = ref(null)

    const search = ref('')

    const columnFilters = ref({})

    const setColumnFilter = (key, value) => {
      columnFilters.value = { ...columnFilters.value, [key]: value || undefined }
    }

    const activeColumnFilters = computed(() =>
      Object.entries(columnFilters.value)
        .filter(([, v]) => !!v)
        .map(([key, value]) => ({ key, label: MATCH_COLUMN_LABELS[key] || key, value }))
    )

    const selectedItems = ref([])

    const activeItems = computed(() => {
      if (selectedItems.value.includes(activeBDX.value.id))
        return selectedItems.value

      return !!activeBDX.value.id
        ? selectedItems.value.concat(activeBDX.value.id)
        : selectedItems.value
    })

    watch(selectedItems, async (newSelectedItems) => {
      await nextTick()

      if (!listRef.value) return

      if (1 < newSelectedItems.length) {
        const defaultContinuousContract = newSelectedItems.every(({continuous_contract}) => {
            return continuous_contract;
        });
        resetActiveBDX()
        activeBDX.value.continuous_contract = defaultContinuousContract;
        
      } else {
        const transformedItems = newSelectedItems
          .map((selectedItem) => {
            const item = listRef.value.items.find(
              (item) => item.id === selectedItem
            )

            return item
          })
          .filter((item) => !!item)

        setActiveBDX(transformedItems[0])
      }
    })

    // Edit modal
    const showEditModal = ref(false)

    const onEditModalSuccess = async () => {
      if (!!listRef.value) await listRef.value.fetchItems()

      selectedItems.value = []
    }

    watch(
      activeBDX,
      (newActiveBDX) => {
        if (!!selectedItems.value.length) return

        showEditModal.value = !!newActiveBDX.id
      },
      { deep: true }
    )

    watch(showEditModal, (newShowEditModal) => {
      if (!newShowEditModal) {
        selectedItems.value = []

        resetActiveBDX()
      }
    })

    provide(BDXMatchPendingManagerInjectionKey, {
      search,
      columnFilters,
      setColumnFilter
    })

    return {
      activeBDX,
      setActiveBDX,

      listRef,

      search,
      columnFilters,
      setColumnFilter,
      activeColumnFilters,

      selectedItems,
      activeItems,

      showEditModal,
      onEditModalSuccess
    }
  }
})
</script>

<style scoped>
.base-search-input {
  max-width: 300px;
}

.active-filters {
  gap: 8px;
}

.active-filters >>> .filter-badge {
  padding: 4px 16px;
  font-size: 12px;
  font-weight: 600;
}
</style>
